import { useState } from 'react';
import axios from 'axios';
import Session from '../../../../api/session';
import Title from '../../../components/Title';
import Navbar from '../../../../layout/logged_in/Navbar';
import Sidenav from '../../../../layout/logged_in/Sidenav';
import Button from '../../../components/Button';
import { backendUrl } from '../../../../config';

const SeoImport = () => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState([]);

    const handleFileChange = (event) => {
        setFile(event.target.files[0] || null);
        setMessage('');
        setErrors([]);
    };

    const handleUpload = async () => {
        if (!file) {
            setMessage('Please select a file to upload.');
            window.showToast('WCL20512');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true);
            setMessage('');
            setErrors([]);

            const response = await axios.post(`${backendUrl}/a/seo/import`, formData, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data?.success) {
                const updatedCount = response.data.updated || 0;
                const errorCount = response.data.errors?.length || 0;
                
                if (updatedCount > 0 && errorCount === 0) {
                    setMessage(`Successfully updated ${updatedCount} row${updatedCount !== 1 ? 's' : ''}.`);
                    window.showToast('WCL20513');
                } else if (updatedCount > 0 && errorCount > 0) {
                    setMessage(`Updated ${updatedCount} row${updatedCount !== 1 ? 's' : ''} with ${errorCount} error${errorCount !== 1 ? 's' : ''}.`);
                    window.showToast('WCL20514');
                } else if (errorCount > 0) {
                    setMessage('Import failed. Please check the errors below.');
                    window.showToast('WCL20515');
                } else {
                    setMessage('No rows were updated. Please check your file.');
                    window.showToast('WCL20514');
                }
                
                setErrors(response.data.errors || []);
                setFile(null);
                document.querySelector('input[type="file"]').value = '';
            } else {
                setMessage(response.data?.message || 'Upload failed. Please check the file format.');
                window.showToast('WCL20514');
            }
        } catch (error) {
            console.error(error);
            const errorMsg = error?.response?.data?.message || 'Upload failed. Please try again.';
            setMessage(errorMsg);
            setErrors(error?.response?.data?.errors || []);
            window.showToast('WCL20515');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Session />
            <Title set="SEO Import" />
            <div className="App">
                <div className="antialiased">
                    <Navbar />
                    <Sidenav />
                    <main className="p-4 lg:pl-[270px] h-screen max-w-screen-2xl pt-20 z-0 mx-auto">
                        <h2 className="text-3xl text-kb-400 tracking-wide font-light py-7 uppercase">SEO Import</h2>
                        <div className="border border-dashed border-gray-200 bg-gray-50 p-6 space-y-4 max-w-2xl">
                            <p className="text-gray-700">Upload an Excel or CSV file to update SEO title and meta description values in bulk.</p>
                            <div className="space-y-2">
                                <div className="text-sm text-gray-600">Accepted file types: <strong>.xlsx</strong>, <strong>.csv</strong></div>
                                <div className="text-sm text-gray-600">Required columns: <strong>Page Type</strong>, <strong>URL</strong>, <strong>Current Title Tag</strong>, <strong>Current Meta Description</strong>.</div>
                                <div className="text-sm text-gray-600"><strong>Leave Title Tag or Meta Description blank to revert to default values.</strong></div>
                                <div className="text-sm text-gray-600">Rows with <strong>#N/A</strong>  will be skipped for that field.</div>
                                <div className="text-sm text-gray-600">Max file size: <strong>10 MB</strong></div>
                                <div className="text-sm text-gray-600">Supported page types: <strong>Homepage</strong>, <strong>Category</strong>, <strong>Collection</strong>, <strong>Product</strong>, <strong>Fabric</strong>.</div>
                            </div>

                            <div className="space-y-3">
                                <label className="block text-sm font-medium text-gray-700">SEO File</label>
                                <input
                                    type="file"
                                    accept=".xlsx,.csv"
                                    onChange={handleFileChange}
                                    disabled={loading}
                                    className="block w-full text-sm text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-kb-300 file:text-white hover:file:bg-kb-400 disabled:opacity-50"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <Button text={loading ? 'Uploading...' : 'UPLOAD FILE'} onClick={handleUpload} disabled={loading || !file} />
                            </div>

                            {message && (
                                <div className={`text-sm p-3 rounded ${errors.length > 0 ? 'bg-yellow-50 text-yellow-800' : 'bg-green-50 text-green-800'}`}>
                                    {message}
                                </div>
                            )}
                            
                            {errors.length > 0 && (
                                <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                    <div className="font-semibold mb-2">Import errors ({errors.length}):</div>
                                    <ul className="list-disc list-inside space-y-1 max-h-60 overflow-y-auto">
                                        {errors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </main>
                </div>
            </div>
        </>
    );
};

export default SeoImport;
